import { parseJwt } from 'src/core/services/jwt';
import { AUTH_TOKEN_KEYS } from 'src/constants/authToken';

/** What the Bills page does with a link that names a company (LB-6046). */
export const BILL_LINK_ACTIONS = {
  /** The link names no company, or this one: open it as before. */
  open: 'open',
  /** Livable staff on another company: switch to the bill's company first, then open it. */
  switchCompany: 'switchCompany',
  /** Anyone else on another company: say so plainly instead of failing to load the bill. */
  otherCompany: 'otherCompany'
};
const sameId = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();

/**
 * Bill emails link to /bills?billId=…&companyId=… (and batch links carry the company the same way).
 * Without the company, the page opened the bill in whichever company the reader had selected, which for
 * staff working across many companies was usually the wrong one: the bill's file and allocations are
 * read through the selected company, so the bill failed to load.
 */
export const billLinkAction = _ref => {
  let linkCompanyId = _ref.linkCompanyId,
    sessionCompanyId = _ref.sessionCompanyId,
    selectedCompanyId = _ref.selectedCompanyId,
    isStaff = _ref.isStaff;
  if (!linkCompanyId) {
    return BILL_LINK_ACTIONS.open;
  }

  // Both have to be the link's company: the token is what the API scopes a call to, and the selection is
  // what the page reads the bill's list, file and allocations through. They can part (a staff session
  // whose company could not be restored), and opening on the token alone would load the bill through
  // the other company -- the very failure this link exists to avoid.
  const onLinkCompany = sessionCompanyId && sameId(linkCompanyId, sessionCompanyId) && (!selectedCompanyId || sameId(linkCompanyId, selectedCompanyId));
  if (onLinkCompany) {
    return BILL_LINK_ACTIONS.open;
  }
  return isStaff ? BILL_LINK_ACTIONS.switchCompany : BILL_LINK_ACTIONS.otherCompany;
};

/**
 * The company the session is acting as: the token's, which is what the API scopes every call to. A staff
 * session without one reads as none, so any link with a company switches it.
 */
export const sessionCompanyIdFromToken = () => {
  var _parseJwt, _localStorage$getItem;
  const companyId = (_parseJwt = parseJwt((_localStorage$getItem = localStorage.getItem(AUTH_TOKEN_KEYS.accessToken)) !== null && _localStorage$getItem !== void 0 ? _localStorage$getItem : '')) === null || _parseJwt === void 0 ? void 0 : _parseJwt['custom:companyid'];
  return companyId && companyId !== 'null' && companyId !== 'undefined' ? companyId : null;
};

/**
 * The existing switch-and-continue page (the one Apply Billing uses): it asks before switching, then
 * returns to this same link, now in the right company.
 */
export const companySwitchUrl = (companyId, _ref2) => {
  let pathname = _ref2.pathname,
    search = _ref2.search;
  return "/companyredirect/".concat(encodeURIComponent(companyId), "?redirectedFrom=").concat(encodeURIComponent("".concat(pathname).concat(search)));
};

/**
 * Leaves for the switch page; a full load, as the switch page itself reloads once it has switched. Replaces
 * the entry rather than adding one: Back from the switch page would otherwise land on the link, which
 * forwards straight back to it.
 */
export const goToCompanySwitch = companyId => window.location.replace(companySwitchUrl(companyId, window.location));